// src/hooks/useModalA11y.js
import { useEffect, useRef } from "react";

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const BODY_PROPS = ["position", "top", "left", "right", "width", "overflow"];
const RAIZ = "[data-invent-root]";

const enfocables = (contenedor) => [...contenedor.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);

function bloquearScroll() {
  const raiz = document.querySelector(RAIZ);
  const contenedores = [];
  for (let n = raiz?.parentElement; n && n !== document.body; n = n.parentElement) {
    if (/(auto|scroll)/.test(window.getComputedStyle(n).overflowY)) contenedores.push(n);
  }
  if (raiz) contenedores.push(raiz);

  const previos = contenedores.map((el) => ({ el, scrollTop: el.scrollTop, overflow: el.style.overflow }));
  previos.forEach(({ el }) => { el.style.overflow = "hidden"; el.scrollTop = 0; });

  const scrollY = window.scrollY || window.pageYOffset;
  const body = document.body.style;
  const bodyPrev = BODY_PROPS.map((p) => body[p]);
  Object.assign(body, { position: "fixed", top: `-${scrollY}px`, left: "0", right: "0", width: "100%", overflow: "hidden" });

  return () => {
    BODY_PROPS.forEach((p, i) => { body[p] = bodyPrev[i]; });
    try {
      window.scrollTo(0, scrollY);
    } finally {
      previos.forEach(({ el, scrollTop, overflow }) => { el.style.overflow = overflow; el.scrollTop = scrollTop; });
    }
  };
}

export function useModalA11y(onClose) {
  const modalRef = useRef(null);
  const inicialRef = useRef(null);

  useEffect(() => {
    const previo = document.activeElement;
    const restaurarScroll = bloquearScroll();
    
    // Foco inicial
    setTimeout(() => {
      (inicialRef.current ?? (modalRef.current && enfocables(modalRef.current)[0]))?.focus();
    }, 10);

    // Solo manejamos el Tab (Focus Trap) aquí
    const manejarTab = (e) => {
      if (e.key !== "Tab" || !modalRef.current) return;
      const nodos = enfocables(modalRef.current);
      if (nodos.length === 0) { e.preventDefault(); return; }
      const primero = nodos[0];
      const ultimo = nodos[nodos.length - 1];
      const activo = document.activeElement;
      const dentro = modalRef.current.contains(activo);
      
      if (e.shiftKey && (!dentro || activo === primero)) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && (!dentro || activo === ultimo)) { e.preventDefault(); primero.focus(); }
    };
    
    document.addEventListener("keydown", manejarTab);

    return () => {
      document.removeEventListener("keydown", manejarTab);
      restaurarScroll();
      if (previo?.isConnected && typeof previo.focus === "function") previo.focus({ preventScroll: true });
    };
  }, []);

  return { modalRef, inicialRef };
}